import { css } from '@emotion/react';
import Input from '@polestar/component-warehouse-react/components/input/Input';
import React, { forwardRef, useCallback } from 'react';
import { useFocusTriggers } from '@polestar/component-warehouse-react/hooks/useFocusTriggers';
import FormField, { minimalFormInputFieldHeight, } from '@polestar/component-warehouse-react/components/form-field/FormField';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import Spacings from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import styled from '@emotion/styled';
import { focusProviderSkipProps } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import Directions from '@polestar/component-warehouse-react/tokens/directions/Directions';
const TextField = forwardRef(({ label, hint, error, errorMessage, trailing, onChange, onBlur, onFocus, name, tooltip, fieldProps = {}, ...props }, ref) => {
    const memoizedId = useSSRSafeId('text-field', props.id);
    const memoizedFieldId = `${memoizedId}-container`;
    const handleChange = useCallback((e) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e.target.value, name, e);
    }, [onChange, name]);
    const { focused, focusBlurEvents } = useFocusTriggers({
        name,
        onFocus,
        onBlur,
    });
    const { isRTL } = useDirection();
    return (React.createElement(FormField, { ...fieldProps, id: memoizedFieldId, label: label, hint: hint, forId: memoizedId, error: error, errorMessage: errorMessage || (typeof error === 'string' && error), trailing: trailing, focused: focused, disabled: props.disabled, required: props.required, tooltip: tooltip },
        React.createElement(StyledInputField, { ...focusProviderSkipProps, ...props, ...focusBlurEvents, ...aria({
                invalid: !!errorMessage || !!error,
                labelledby: (props === null || props === void 0 ? void 0 : props['aria-labelledby']) || (!!label ? `${memoizedId}-label` : undefined),
                describedby: (props === null || props === void 0 ? void 0 : props['aria-describedby']) ||
                    (!!hint || !!error ? `${memoizedId}-message` : undefined),
                disabled: props.disabled,
            }), name: name, ref: ref, id: memoizedId, onChange: handleChange, dir: props.type === 'email' || props.type === 'tel' ? Directions.ltr : undefined, forcedLTR: isRTL && (props.type === 'email' || props.type === 'tel') ? true : false })));
});
TextField.displayName = 'TextField';
export default TextField;
const StyledInputField = styled(Input)(({ hover, focused, error, disabled, forcedLTR, theme }) => css(css `
        width: 100%;
        color: ${theme.formField.states.enabled.inputTextColor};
        transition: ${createCSSTransition('color', MotionEasings.default, MotionDurations.fast)};
        /* this is needed while Safari adds 2px top/bottom margin by default */
        margin: 0;
        flex-grow: 1;
        padding: ${Spacings.small} ${Spacings.medium};

        &::-webkit-inner-spin-button {
          display: none;
        }

        &:-webkit-autofill,
        &:-webkit-autofill:hover,
        &:-webkit-autofill:focus {
          appearance: none;
          transition: background-color 9999s ease-in-out 0s;
          -webkit-box-shadow: 0 0 0 1000px ${theme.formField.backgroundColor} inset;
        }

        &:focus,
        &:required,
        &:invalid {
          box-shadow: none;
          outline: none;
        }

        &::placeholder {
          color: ${theme.formField.states.enabled.placeholderColor};
        }
      `, forcedLTR &&
    css `
          text-align: end;
        `, hover &&
    css `
          color: ${theme.formField.states.hover.inputTextColor};
        `, focused &&
    css `
          color: ${theme.formField.states.focused.inputTextColor};
          cursor: text;
        `, error &&
    css `
          color: ${theme.formField.states.error.inputTextColor};
        `, disabled &&
    css `
          color: ${theme.formField.states.disabled.inputTextColor};
        `, minimalFormInputFieldHeight));
